import React, { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Minimal, dependency-free Google Maps probe. No routes, markers, traffic,
// incidents or API calls — one map, one coordinate. Never falls back to Leaflet.
// It surfaces the EXACT loader/auth error so a Google config problem cannot hide.
export default function MapsDiagnostic() {
  const ref = useRef(null);
  const [status, setStatus] = useState("loading");
  const [reason, setReason] = useState("");
  const [log, setLog] = useState([]);
  const push = (m) => setLog((l) => [...l, `${new Date().toISOString().slice(11, 19)}  ${m}`]);

  const fp = MAPS_KEY ? `${MAPS_KEY.slice(0, 5)}…${MAPS_KEY.slice(-4)}  ·  length ${MAPS_KEY.length}` : "MISSING / EMPTY";

  useEffect(() => {
    push(`Host: ${window.location.host}`);
    push(`Browser key (REACT_APP_GOOGLE_MAPS_API_KEY): ${fp}`);
    if (!MAPS_KEY) { setStatus("error"); setReason("REACT_APP_GOOGLE_MAPS_API_KEY is empty in this build."); return; }
    window.gm_authFailure = () => {
      setStatus("error");
      setReason("gm_authFailure — Google rejected the key for THIS domain. Open the browser Console for the exact code (RefererNotAllowedMapError / InvalidKeyMapError / ApiNotActivatedMapError / BillingNotEnabledMapError / OverQuotaMapError).");
      push("window.gm_authFailure FIRED");
    };
    setOptions({ key: MAPS_KEY, v: "weekly" });
    push("setOptions() done · calling importLibrary('maps') …");
    importLibrary("maps").then(() => {
      push("importLibrary('maps') resolved · google.maps present: " + !!(window.google && window.google.maps));
      const g = window.google;
      const map = new g.maps.Map(ref.current, { center: { lat: 51.5074, lng: -0.1278 }, zoom: 10 });
      new g.maps.Marker({ position: { lat: 51.5074, lng: -0.1278 }, map, title: "Diagnostic marker" });
      setStatus("ok");
      push("Map + one fixed marker created at London (51.5074, -0.1278). If tiles are blank but no gm_authFailure, it's a sizing/CSS issue, not the key.");
    }).catch((e) => {
      setStatus("error");
      setReason("importLibrary threw: " + (e && e.message ? e.message : String(e)));
      push("importLibrary('maps') REJECTED: " + (e && e.message ? e.message : String(e)));
    });
  }, []); // eslint-disable-line

  return (
    <div style={{ padding: 24, fontFamily: "system-ui, sans-serif", maxWidth: 900, margin: "0 auto" }} data-testid="maps-diagnostic">
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Google Maps Diagnostic</h1>
      <p style={{ color: "#64748b", marginBottom: 16 }}>Minimal Google map · no routes/markers/traffic · no Leaflet fallback.</p>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
        <div><div style={{ fontSize: 11, textTransform: "uppercase", color: "#94a3b8", fontWeight: 700 }}>Status</div>
          <div data-testid="diag-status" style={{ fontWeight: 800, color: status === "ok" ? "#059669" : status === "error" ? "#b91c1c" : "#334155" }}>{status.toUpperCase()}</div></div>
        <div><div style={{ fontSize: 11, textTransform: "uppercase", color: "#94a3b8", fontWeight: 700 }}>Browser key</div>
          <div style={{ fontFamily: "monospace" }}>{fp}</div></div>
      </div>
      {status === "error" && (
        <pre data-testid="diag-error" style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, whiteSpace: "pre-wrap" }}>
{`Google Maps failed to initialise\nReason: ${reason}`}
        </pre>
      )}
      <div ref={ref} style={{ width: "100%", height: 420, border: "1px solid #cbd5e1", borderRadius: 12, marginTop: 12, background: "#f1f5f9" }} data-testid="diag-map" />
      <pre style={{ marginTop: 12, background: "#0f172a", color: "#e2e8f0", padding: 12, borderRadius: 8, fontSize: 12, whiteSpace: "pre-wrap" }} data-testid="diag-log">{log.join("\n")}</pre>
    </div>
  );
}
